import { useCallback, useEffect, useMemo, useState } from 'react';

import apiCaller from 'services/Api/ApiCaller';
import { API_URL } from 'settings';
import type { LaunchSummary } from 'state';
import type { AssessmentRegeneration } from '../AdminDashboard/Blueprints/api';
import {
  apiErrorFrom,
  readableError,
} from '../AdminDashboard/ScenarioBuilder/errors';

export const PREPARATION_POLL_MS = 3000;

type Preparation = {
  variant_id: number;
  regeneration: AssessmentRegeneration;
  launch_summary: LaunchSummary;
};

type Snapshot = {
  context: unknown;
  result?: Preparation;
  preparing: boolean;
  error: string;
};

/** Prepare only the chosen exercise. Pending requests are reused by the server. */
export default function useBlueprintPreparation({
  blueprintId,
  variantId,
  enabled,
  revision,
  refreshToken,
}: {
  blueprintId?: number;
  variantId?: number;
  enabled: boolean;
  revision?: string;
  refreshToken?: unknown;
}) {
  const [retryToken, setRetryToken] = useState(0);
  const context = useMemo(
    () => ({
      blueprintId,
      variantId,
      revision,
      enabled,
      retryToken,
      refreshToken,
    }),
    [blueprintId, variantId, revision, enabled, retryToken, refreshToken],
  );
  const [snapshot, setSnapshot] = useState<Snapshot>({
    context: '',
    preparing: false,
    error: '',
  });
  const retry = useCallback(() => setRetryToken(value => value + 1), []);
  const active = enabled && Boolean(blueprintId && variantId);

  useEffect(() => {
    if (!active || !blueprintId || !variantId) return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const controller = new AbortController();
    setSnapshot({ context, preparing: true, error: '' });

    const prepare = async () => {
      try {
        const response = await apiCaller({
          path: `/blueprints/${blueprintId}/prepare_assessment`,
          method: 'POST',
          data: { variant_id: variantId },
          apiUrl: API_URL,
          signal: controller.signal,
        });
        if (cancelled) return;
        if (response.status < 200 || response.status >= 300) {
          throw apiErrorFrom(response.status, response.result);
        }
        const result = response.result as Preparation;
        if (
          result?.variant_id !== variantId ||
          !result.launch_summary ||
          !['queued', 'running', 'ready', 'idle', 'failed', 'blocked'].includes(
            result.regeneration?.status,
          )
        ) {
          throw new Error(
            'The assessment preparation response could not be verified.',
          );
        }
        // HTTP 200 is the authoritative participation decision, even when an
        // answer key is still generating. Runtime refusals must also stop
        // polling; a new answer key cannot supply a missing trading quote.
        // Older 202 responses keep polling even if they carry stale cards.
        const terminal = response.status === 200;
        const preparing =
          !terminal &&
          ['queued', 'running'].includes(result.regeneration.status);
        if (
          !preparing &&
          typeof result.launch_summary.launchable !== 'boolean'
        ) {
          throw new Error(
            'The assessment launch decision could not be verified.',
          );
        }
        setSnapshot({ context, result, preparing, error: '' });
        if (preparing)
          timer = setTimeout(() => void prepare(), PREPARATION_POLL_MS);
      } catch (error) {
        if (cancelled) return;
        setSnapshot({
          context,
          preparing: false,
          error: readableError(error, { what: 'Assessment preparation' }),
        });
      }
    };
    void prepare();
    return () => {
      cancelled = true;
      controller.abort();
      if (timer) clearTimeout(timer);
    };
  }, [active, blueprintId, variantId, context]);

  const current = active && snapshot.context === context;
  const preparing = active && (!current || snapshot.preparing);
  const result = current && !preparing ? snapshot.result : undefined;
  return {
    preparing,
    summary: result?.launch_summary,
    regeneration: current ? snapshot.result?.regeneration : undefined,
    failed: ['failed', 'blocked'].includes(result?.regeneration.status || ''),
    error: current ? snapshot.error : '',
    retry,
  };
}
